/* eslint-disable unicorn/no-array-for-each */

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────

export const useLoginForm = () => {
  const { t } = useLocalizationContext();

  // ────────────────────────────────────────────────────────────────────

  const requiredEmailMessage = t('form.email-required');
  const requiredPasswordMessage = t('form.password-required');
  const invalidEmail = t('form.validation-email');

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          email: yup
            .string()
            .required(requiredEmailMessage)
            .email(invalidEmail),
          password: yup.string().required(requiredPasswordMessage),
        })
        .required(),
    [invalidEmail, requiredEmailMessage, requiredPasswordMessage],
  );

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const showVerificationErrors = useCallback(
    (fieldsErrors: LoginFieldsErrors) => {
      fieldsErrors.forEach((fieldError) => {
        const { field, message } = fieldError;

        const fieldName = field as keyof LoginFormData;

        form.setError(fieldName, { type: 'validate', message });
      });
    },
    [form],
  );

  // ────────────────────────────────────────────────────────────────────

  return { form, showVerificationErrors };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type LoginFormData = Readonly<{
  email: string;
  password: string;
  grecaptcha?: string;
}>;

type LoginFieldsErrors = ReadonlyArray<
  Readonly<{
    field: string;
    message: string;
  }>
>;
